import React, { useEffect, useState } from 'react'
import { useRouter } from 'next/router'
import { useQuery } from 'react-query'

import { BookEnum } from '@interfaces/BookEnum'
import { usePagination } from '@hooks/usePagination'
import { initialValues } from '@constants/form/myWritingSearchForm/initialValues'
import { useMyWritingAction } from '@hooks/book/myWriting/useMyWritingAction'
import { useAuthentication } from '@hooks/useAuthentication'
import { scrollToElement } from '@lib/utils'
import { SearchFormType } from './components/SearchForm/interface'
import { MyEbookTabProps } from './interface'

export function withMyEbookTab(Component: React.FC<MyEbookTabProps>) {
  function WithMyEbookTab() {
    const router = useRouter()
    const myWritingClient = useMyWritingAction()
    const { isAuthenticated } = useAuthentication()
    const advanceSearch = router.query.advanceSearch === 'true'
    const { page, perpage, pageChange, perpageChange } = usePagination()
    const [searchParam, setSearchParam] =
      useState<SearchFormType>(initialValues)

    const { data: myEbook, isLoading } = useQuery(
      ['my-ebook', page, perpage, searchParam],
      () => myWritingClient.getMyEbook(page, perpage, searchParam),
      {
        enabled: isAuthenticated && (router.query.tab as string) === 'myEbook',
      }
    )

    async function handleSubmit(val: SearchFormType) {
      pageChange(1)

      const query: any = { tab: 'myEbook', advanceSearch }
      if (val.param.length) query.text = val.param.trim()
      if (val.bookType.length) query.bookType = val.bookType
      if (val.category.length) query.category = val.category
      if (val.penName.length) query.penName = val.penName

      router.replace({
        pathname: '/my-writing',
        query,
      })
    }

    function handleViewDetail(bookId: number) {
      router.push({
        pathname: `${router.pathname}/${bookId}/ebook`,
      })
    }

    function handlePaginationChange(newPage: number) {
      pageChange(newPage)
      scrollToElement('#my-writing')
    }

    useEffect(() => {
      const param = (router.query.text as string) || ''
      const bookType = router.query.bookType
        ? typeof router.query.bookType === 'string'
          ? ([router.query.bookType] as BookEnum[])
          : (router.query.bookType as BookEnum[])
        : []
      const category = router.query.category
        ? typeof router.query.category === 'string'
          ? [router.query.category]
          : router.query.category
        : []
      const penName = router.query.penName
        ? typeof router.query.penName === 'string'
          ? [router.query.penName]
          : router.query.penName
        : []

      setSearchParam({
        param,
        bookType,
        category,
        penName,
      })
    }, [
      router.query.text,
      router.query.bookType,
      router.query.category,
      router.query.penName,
    ])

    const componentProps = {
      initialValues: searchParam,
      total: myEbook?.total || 0,
      myEbookList: myEbook?.data || [],
      isLoading,
      page,
      perpage,
      pageChange: handlePaginationChange,
      perpageChange,
      handleSubmit,
      handleViewDetail,
    }

    return <Component {...componentProps} />
  }

  return WithMyEbookTab
}
